import React, { useEffect, useRef, useMemo } from 'react';
import { useLanguage } from '../../../../../context/LanguageContext';
import '../style/why-choose-section.css';
import { initializeWhyChooseSection } from '../script/why-choose-section';
import { initializeTitleAnimation } from '../script/title-animation';
import whyChooseBgImage from '../../../../../assets/logo/why_choose_us/roady projeto site 2025 imagem 1220 x 810.webp';

const WhyChooseSection: React.FC = () => {
    const { t, language } = useLanguage();
    const containerRef = useRef<HTMLElement>(null);

    // Access translations - recalculate when language changes to ensure re-render
    const translation = useMemo(() => t as any, [t, language]);
    const whyChooseTranslations = useMemo(() => translation.WHY_CHOOSE_US, [translation, language]);
    const aboutTranslations = useMemo(() => translation.ABOUT, [translation, language]);

    useEffect(() => {
        if (!containerRef.current) {
            return;
        }

        const hasInitialized = sessionStorage.getItem('whychoose-titles-initialized');
        
        // Initialize title animation first
        const titleTimer = setTimeout(() => {
            if (!hasInitialized) {
            initializeTitleAnimation();
                sessionStorage.setItem('whychoose-titles-initialized', 'true');
            } else {
                // On language change, just make titles visible without animation
                const titles = document.querySelectorAll('.why-choose-one .title-animation');
                titles.forEach((title: Element) => {
                    const el = title as HTMLElement;
                    el.style.opacity = '1';
                });
            }
            
            // Refresh ScrollTrigger
            if (typeof window !== 'undefined' && (window as any).ScrollTrigger) {
                (window as any).ScrollTrigger.refresh();
            }
        }, 100);

        // Initialize marquee after a delay to ensure DOM is ready
        const timer = setTimeout(() => {
            initializeWhyChooseSection(containerRef.current);
        }, 1000);

        return () => {
            clearTimeout(timer);
            clearTimeout(titleTimer);
        };
    }, [language]); // Re-run when language changes

    return (
        <section className="why-choose-one" ref={containerRef}>
            <ul className="why-choose-one__sliding-text-list list-unstyled marquee_mode-1">
                <li>
                    <span data-hover="Branding" className="why-choose-one__sliding-text-title">
                        {whyChooseTranslations?.slidingText ?? 'PNEUS PARA TODAS AS ESTAÇÕES'}
                    </span>
                </li>
            </ul>
            <div 
                className="why-choose-one__right-bg-color" 
                style={{ opacity: 0.9, backgroundColor: '#ea2127', zIndex: -1 }}
            ></div>
            <div 
                className="why-choose-one__right-bg"
                style={{
                    backgroundImage: `url(${whyChooseBgImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    filter: 'none',
                    mixBlendMode: 'normal' as React.CSSProperties['mixBlendMode']
                } as React.CSSProperties}
            ></div>
            <div className="container">
                <div className="row">
                    <div className="col-xl-6">
                        <div className="why-choose-one__left">
                            <div className="section-title text-left sec-title-animation animation-style2">
                                <div className="section-title__tagline-box">
                                    <div className="section-title__tagline-border">
                                        <div className="section-title__shape-1">
                                            <i className="section-title__circle"></i>
                                        </div>
                                    </div>
                                    <h6 className="section-title__tagline">
                                        {whyChooseTranslations?.tagline ?? 'PNEUS PARA TODAS AS ESTAÇÕES'}
                                    </h6>
                                    <div className="section-title__tagline-border">
                                        <div className="section-title__shape-2">
                                            <i className="section-title__circle"></i>
                                        </div>
                                    </div>
                                </div>
                                <h3 
                                    className="section-title__title title-animation"
                                    key={`whychoose-title-${language}`}
                                    dangerouslySetInnerHTML={{ 
                                        __html: (whyChooseTranslations?.title ?? 'Marcas de confiança, preços competitivos e segurança em cada quilómetro.').replace(
                                            /preços competitivos/gi, 
                                            '<span style="color: #ea2127;">preços competitivos</span>'
                                        ).replace(
                                            /competitive prices/gi, 
                                            '<span style="color: #ea2127;">competitive prices</span>'
                                        )
                                    }}
                                ></h3>
                            </div>
                            <p 
                                className="why-choose-one__text"
                                dangerouslySetInnerHTML={{ __html: whyChooseTranslations?.text ?? 'No Roady Chaves encontra uma ampla seleção de pneus<br>das principais marcas, desde opções acessíveis a gamas premium.<br>Com aconselhamento técnico, montagem profissional<br>e equilibragem precisa, garantimos desempenho,<br>segurança e confiança em todas as estações do ano.' }}
                            ></p>
                            <div className="why-choose-one__points-box">
                                <div className="why-choose-one__points-list-shape-1"></div>
                                <ul className="why-choose-one__points-list list-unstyled">
                                    <li>
                                        <div className="icon">
                                            <span className="icon-tire"></span>
                                        </div>
                                        <p>{whyChooseTranslations?.satisfactionGuarantee ?? 'As melhores marcas internacionais de pneus'}</p>
                                    </li>
                                    <li>
                                        <div className="icon">
                                            <span className="icon-clock-1"></span>
                                        </div>
                                        <p>{aboutTranslations?.expressOilChange ?? 'Mudança de óleo express'}</p>
                                    </li>
                                </ul>
                                <ul className="why-choose-one__points-list list-unstyled" style={{ marginRight: '120px' }}>
                                    <li>
                                        <div className="icon">
                                            <span className="icon-tools"></span>
                                        </div>
                                        <p>{aboutTranslations?.qualityControlSystem ?? 'Sistema interno de controlo de qualidade'}</p>
                                    </li>
                                    <li>
                                        <div className="icon">
                                            <span className="icon-technician"></span>
                                        </div>
                                        <p>{whyChooseTranslations?.affordableCost ?? 'Equipa técnica certificada'}</p>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-6">
                        <div className="why-choose-one__right">
                            <div className="row">
                                <div className="col-xl-6 col-lg-6 col-md-6 wow fadeInLeft" data-wow-delay="100ms">
                                    <div className="why-choose-one__count-single">
                                        <div className="why-choose-one__count-icon">
                                            <span className="icon-tire"></span>
                                        </div>
                                        <div className="why-choose-one__count-content">
                                            <p className="why-choose-one__count-text">
                                                {aboutTranslations?.bestTireBrands ?? 'As melhores marcas internacionais de pneus'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xl-6 col-lg-6 col-md-6 wow fadeInRight" data-wow-delay="200ms">
                                    <div className="why-choose-one__count-single">
                                        <div className="why-choose-one__count-icon">
                                            <span className="icon-oil"></span>
                                        </div>
                                        <div className="why-choose-one__count-content">
                                            <p className="why-choose-one__count-text">
                                                {aboutTranslations?.expressOilChange ?? 'Mudança de óleo express'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xl-6 col-lg-6 col-md-6 wow fadeInLeft" data-wow-delay="300ms">
                                    <div className="why-choose-one__count-single">
                                        <div className="why-choose-one__count-icon">
                                            <span className="icon-check-1"></span>
                                        </div>
                                        <div className="why-choose-one__count-content">
                                            <p className="why-choose-one__count-text">
                                                {aboutTranslations?.qualityControlSystem ?? 'Sistema interno de controlo de qualidade'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xl-6 col-lg-6 col-md-6 wow fadeInRight" data-wow-delay="400ms">
                                    <div className="why-choose-one__count-single">
                                        <div className="why-choose-one__count-icon">
                                            <span className="icon-expert-team"></span>
                                        </div>
                                        <div className="why-choose-one__count-content">
                                            <p className="why-choose-one__count-text">
                                                {whyChooseTranslations?.certifiedTeam ?? 'Equipa técnica certificada'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default WhyChooseSection;

